<template>
  <div class="question-screen">
    <ProgressHeader
      :current="current"
      :total="total"
      tone="green"
      @close="$emit('close')"
    />

    <CircularTimer
      :remaining="remaining"
      :progress="progress"
    />

    <section class="question-card question-card--cyan question-card--main question-card--c">
      <h2 class="question-card__prompt">
        {{ question.prompt }}
      </h2>

      <ProblemVisual
        v-if="question.imageSrc || question.visualType"
        :visual-type="question.imageSrc ? 'none' : question.visualType"
        :image-src="question.imageSrc"
        :image-alt="question.imageAlt"
      />
    </section>

    <p class="c-instruction">
      {{ question.instruction }}
    </p>

    <div class="choice-list choice-list--c">
      <ChoiceButton
        v-for="choice in question.choices"
        :key="choice.id"
        :choice="choice"
        :selected="selectedChoiceId === choice.id"
        :disabled="locked"
        @select="select(choice.id, true)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, toRef, watch } from 'vue'
import CircularTimer from '@/components/CircularTimer.vue'
import ChoiceButton from '@/components/ChoiceButton.vue'
import ProblemVisual from '@/components/ProblemVisual.vue'
import ProgressHeader from '@/components/ProgressHeader.vue'
import { useCountdown } from '@/composables/useCountdown'
import type { Question } from '@/types/content'

const props = defineProps<{
  question: Question
  current: number
  total: number
}>()

const emit = defineEmits<{
  answered: [
    payload: {
      choiceId: string
      selectedByUser: boolean
      elapsedMs: number
    },
  ]
  close: []
}>()

const selectedChoiceId = ref('')
const locked = ref(false)
const active = ref(true)

const duration = toRef(props.question, 'timerSeconds')

const {
  remaining,
  progress,
  stop,
  reset,
  elapsedMs,
} = useCountdown(
  duration,
  active,
  handleTimeout,
)

function timeoutChoiceId(): string {
  if (props.question.timeoutChoiceId === '__last__') {
    return props.question.choices.at(-1)?.id ?? ''
  }

  return props.question.timeoutChoiceId
}

function handleTimeout(): void {
  select(timeoutChoiceId(), false)
}

function select(
  choiceId: string,
  selectedByUser: boolean,
): void {
  if (locked.value || !choiceId) return

  locked.value = true
  active.value = false
  stop()

  selectedChoiceId.value = choiceId

  const elapsed = selectedByUser
    ? elapsedMs()
    : props.question.timerSeconds * 1000

  window.setTimeout(() => {
    emit('answered', {
      choiceId,
      selectedByUser,
      elapsedMs: elapsed,
    })
  }, 450)
}

watch(
  () => props.question.id,
  () => {
    selectedChoiceId.value = ''
    locked.value = false
    active.value = true
    reset()
  },
)
</script>